import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { ArrowLeft, Check, AlertTriangle, Plus, X } from "lucide-react";
import { Badge } from "../../components/shared/Badge";
import {
  usePlatformUser, useSetPlatformRole, useUnlockPlatformUser,
  useResetUserPasswordPlatform, useDeletePlatformUser,
  useRemoveEnterpriseMembership, useAssignPlatformEnterpriseRoles,
  useAddToEnterprise, usePlatformRoles, usePlatformEnterprises,
  useAssignPlatformRoles, useUpdateUserStorageLimit, useEnterpriseStorage,
  useSetDefaultEnterprise,
} from "@platform/hooks";
import { useToast } from "@nthamizh/ui";
import { cn } from "@nthamizh/ui";

interface EnterpriseMembership {
  enterpriseId: string; enterpriseName: string;
  enterpriseSlug: string; status: string;
}
interface EnterpriseRole {
  roleId: string; roleName: string; roleKey: string;
  // null enterpriseId = platform-level role assignment (no enterprise)
  enterpriseId: string | null; enterpriseName: string | null; moduleKey: string | null;
}
interface PlatformUserDetail {
  id: string; name: string; email: string;
  jobTitle: string | null; platformRole: string | null;
  isLocked: boolean; failedLoginAttempts: number; createdAt: string;
  defaultEnterpriseId: string | null;
  enterprises: EnterpriseMembership[];
  roles: EnterpriseRole[];
}
interface PlatformRole { id: string; name: string; key: string; moduleKey: string | null; individuallyAssignable: boolean }
interface Enterprise { id: string; name: string; slug: string }

function SectionCard({ title, action, children }: {
  title: string; action?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-ds-border rounded-card shadow-card p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-semibold text-ds-text-muted uppercase tracking-wide">{title}</p>
        {action}
      </div>
      {children}
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-ds-border last:border-0">
      <span className="text-sm text-ds-text-secondary">{label}</span>
      <span className="text-sm font-medium text-ds-text">{value}</span>
    </div>
  );
}

function RoleCheckList({ roles, selectedIds, onToggle }: {
  roles: PlatformRole[];
  selectedIds: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <div className="divide-y divide-ds-border">
      {roles.map((role) => {
        const checked = selectedIds.includes(role.id);
        return (
          <button key={role.id} onClick={() => onToggle(role.id)}
            className="w-full flex items-center gap-2.5 py-2.5 text-left hover:bg-ds-bg transition-colors">
            <div className={cn(
              "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors",
              checked ? "bg-indigo-600 border-indigo-600" : "border-ds-border-strong"
            )}>
              {checked && <Check className="w-2.5 h-2.5 text-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-ds-text">{role.name}</p>
              {role.moduleKey && (
                <p className="text-xs text-ds-text-muted capitalize">{role.moduleKey} module</p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export function PlatformUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: userData, isLoading } = usePlatformUser(id ?? null);
  const { data: rolesData = [] } = usePlatformRoles();
  const { data: enterprisesData = [] } = usePlatformEnterprises();

  const setPlatformRole = useSetPlatformRole();
  const unlock = useUnlockPlatformUser();
  const resetPw = useResetUserPasswordPlatform();
  const deleteUser = useDeletePlatformUser();
  const removeMembership = useRemoveEnterpriseMembership();
  const assignRoles = useAssignPlatformEnterpriseRoles();
  const addToEnterprise = useAddToEnterprise();
  const assignPlatformRoles = useAssignPlatformRoles();

  const user = userData as PlatformUserDetail | undefined;
  const allRoles = rolesData as unknown as PlatformRole[];
  // Backend-enforced now (PATCH /users/:id/platform-roles rejects any
  // roleId that isn't individuallyAssignable), not just a UI filter —
  // this list is a convenience for the checkbox UI, not the actual
  // access-control boundary. Previously hardcoded to moduleKey ===
  // "design" (ConfigIQ-only); now any role an admin has explicitly
  // marked assignable-without-an-enterprise via the Role Builder.
  const individuallyAssignableRoles = allRoles.filter((r) => r.individuallyAssignable);
  const allEnterprises = enterprisesData as Enterprise[];

  // Platform role state
  const [selectedPlatformRole, setSelectedPlatformRole] = useState<string>("");
  const [platformRoleSynced, setPlatformRoleSynced] = useState(false);

  // Existing enterprise roles editor
  const [activeEnterpriseId, setActiveEnterpriseId] = useState<string | null>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

  // Platform-level roles editor (no enterprise) — Design & Configuration access
  const [platformRoleIds, setPlatformRoleIds] = useState<string[]>([]);
  const [platformRolesSynced, setPlatformRolesSynced] = useState(false);

  // Add to enterprise form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addEnterpriseId, setAddEnterpriseId] = useState<string>("");
  const [addRoleIds, setAddRoleIds] = useState<string[]>([]);

  // Misc
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteText, setDeleteText] = useState("");

  // Storage limit for this user
  const updateStorageLimit = useUpdateUserStorageLimit();
  const setDefaultEnterprise = useSetDefaultEnterprise();
  const [storageLimitMb, setStorageLimitMb] = useState<string>("");
  const [storageLimitSynced, setStorageLimitSynced] = useState(false);
  const firstEnterpriseId = user?.enterprises?.[0]?.enterpriseId ?? null;
  const { data: enterpriseStorage } = useEnterpriseStorage(firstEnterpriseId);

  // Sync initial state from API data once
  if (!platformRoleSynced && user) {
    setSelectedPlatformRole(user.platformRole ?? "");
    setPlatformRoleSynced(true);
    if (user.enterprises.length > 0 && !activeEnterpriseId) {
      const first = user.enterprises[0]!;
      setActiveEnterpriseId(first.enterpriseId);
      setSelectedRoleIds(
        user.roles.filter((r) => r.enterpriseId === first.enterpriseId).map((r) => r.roleId)
      );
    }
  }
  if (!platformRolesSynced && user) {
    setPlatformRoleIds(user.roles.filter((r) => r.enterpriseId === null).map((r) => r.roleId));
    setPlatformRolesSynced(true);
  }

  const changeActiveEnterprise = (eid: string) => {
    setActiveEnterpriseId(eid);
    setSelectedRoleIds((user?.roles ?? []).filter((r) => r.enterpriseId === eid).map((r) => r.roleId));
  };

  // Enterprises this user is NOT already in (for the add form dropdown)
  const memberEnterpriseIds = new Set((user?.enterprises ?? []).map((e) => e.enterpriseId));
  const availableToAdd = allEnterprises.filter((e) => !memberEnterpriseIds.has(e.id));

  const handleSavePlatformRole = async () => {
    if (!user) return;
    try {
      await setPlatformRole.mutateAsync({
        id: user.id,
        platformRole: (selectedPlatformRole as "platform_admin" | "support") || null,
      });
      toast({ title: "Platform role saved", variant: "success" });
    } catch { toast({ title: "Failed to save platform role", variant: "error" }); }
  };

  const handleUnlock = async () => {
    if (!user) return;
    try {
      await unlock.mutateAsync(user.id);
      toast({ title: `${user.name} unlocked`, variant: "success" });
    } catch { toast({ title: "Failed to unlock user", variant: "error" }); }
  };

  const handleResetPassword = async () => {
    if (!user) return;
    try {
      const result = await resetPw.mutateAsync(user.id);
      setTempPassword(result.tempPassword);
    } catch { toast({ title: "Failed to reset password", variant: "error" }); }
  };

  const handleRemoveMembership = async (eid: string, ename: string) => {
    if (!user) return;
    try {
      await removeMembership.mutateAsync({ userId: user.id, enterpriseId: eid });
      toast({ title: `Removed from ${ename}`, variant: "success" });
      if (activeEnterpriseId === eid) {
        const remaining = user.enterprises.filter((e) => e.enterpriseId !== eid);
        const next = remaining[0] ?? null;
        setActiveEnterpriseId(next?.enterpriseId ?? null);
        setSelectedRoleIds(next ? user.roles.filter((r) => r.enterpriseId === next.enterpriseId).map((r) => r.roleId) : []);
      }
    } catch { toast({ title: "Failed to remove membership", variant: "error" }); }
  };

  const handleSaveRoles = async () => {
    if (!user || !activeEnterpriseId) return;
    try {
      await assignRoles.mutateAsync({ userId: user.id, enterpriseId: activeEnterpriseId, roleIds: selectedRoleIds });
      toast({ title: "Roles updated", variant: "success" });
    } catch { toast({ title: "Failed to update roles", variant: "error" }); }
  };

  const handleSavePlatformRoles = async () => {
    if (!user) return;
    try {
      await assignPlatformRoles.mutateAsync({ userId: user.id, roleIds: platformRoleIds });
      toast({ title: "Roles updated", variant: "success" });
    } catch (err: unknown) {
      // Was a bare catch discarding the error entirely — this is the
      // one handler PATCH /users/:id/platform-roles' new
      // individuallyAssignable validation actually reaches, so a
      // generic "Failed to update roles" would silently swallow the
      // specific, useful message the backend now sends (e.g. "Not
      // assignable without an enterprise: Enterprise Admin"), leaving
      // an admin to guess which of their selections was rejected.
      const msg = err instanceof Error ? err.message : "Failed to update roles";
      toast({ title: msg, variant: "error" });
    }
  };

  const handleAddToEnterprise = async () => {
    if (!user || !addEnterpriseId) return;
    if (addRoleIds.length === 0) { toast({ title: "Select at least one role", variant: "error" }); return; }
    try {
      await addToEnterprise.mutateAsync({ userId: user.id, enterpriseId: addEnterpriseId, roleIds: addRoleIds });
      const entName = allEnterprises.find((e) => e.id === addEnterpriseId)?.name ?? "";
      toast({ title: `Added to ${entName}`, variant: "success" });
      // Switch role editor to the new enterprise
      setActiveEnterpriseId(addEnterpriseId);
      setSelectedRoleIds(addRoleIds);
      // Reset add form
      setShowAddForm(false);
      setAddEnterpriseId("");
      setAddRoleIds([]);
    } catch { toast({ title: "Failed to add to enterprise", variant: "error" }); }
  };

  const handleDeleteUser = async () => {
    if (!user || deleteText !== "DELETE") return;
    try {
      await deleteUser.mutateAsync(user.id);
      toast({ title: `${user.name} deleted`, variant: "success" });
      navigate("/platform/users");
    } catch (err: unknown) {
      toast({ title: err instanceof Error ? err.message : "Failed to delete user", variant: "error" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-8 text-center text-sm text-ds-text-muted">
        User not found.{" "}
        <button onClick={() => navigate("/platform/users")} className="text-indigo-600 hover:underline">
          Back to all users
        </button>
      </div>
    );
  }

  const activeEntName = user.enterprises.find((e) => e.enterpriseId === activeEnterpriseId)?.enterpriseName ?? "";

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Back */}
      <button onClick={() => navigate("/platform/users")}
        className="flex items-center gap-1.5 text-sm text-ds-text-secondary hover:text-ds-text transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to all users
      </button>

      {/* Hero */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-base font-semibold flex-shrink-0">
            {user.name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h1 className="text-lg font-semibold text-ds-text">{user.name}</h1>
            <p className="text-sm text-ds-text-secondary">{user.email}</p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {user.isLocked
                ? <Badge variant="red">Locked ({user.failedLoginAttempts} failed attempts)</Badge>
                : <Badge variant="green">Active</Badge>}
              {user.platformRole && <Badge variant="purple">{user.platformRole.replace("_", " ")}</Badge>}
              {user.jobTitle && <Badge variant="gray">{user.jobTitle}</Badge>}
              {user.enterprises.length === 0 && <Badge variant="amber">No enterprise</Badge>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {user.isLocked && (
            <button onClick={handleUnlock} disabled={unlock.isPending}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 text-sm font-medium hover:bg-amber-100 disabled:opacity-50">
              Unlock account
            </button>
          )}
          <button onClick={handleResetPassword} disabled={resetPw.isPending}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white border border-ds-border text-sm font-medium text-ds-text-secondary hover:bg-ds-bg disabled:opacity-50">
            Reset password
          </button>
        </div>
      </div>

      {tempPassword && (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
          <p className="text-xs font-semibold text-amber-700 mb-1">Temporary password</p>
          <p className="text-sm font-mono text-amber-900">{tempPassword}</p>
          <p className="text-xs text-amber-600 mt-1">Share securely. The user must change it on first login.</p>
        </div>
      )}

      {/* Account info + Platform role */}
      <div className="grid grid-cols-2 gap-5">
        <SectionCard title="Account info">
          <MetaRow label="Member since" value={new Date(user.createdAt).toLocaleDateString()} />
          <MetaRow label="Auth method" value="Email + password" />
          <MetaRow label="Failed logins" value={user.failedLoginAttempts} />
          <MetaRow label="Account locked" value={user.isLocked ? <Badge variant="red">Yes</Badge> : "No"} />
        </SectionCard>

        <SectionCard title="Platform role">
          <select value={selectedPlatformRole} onChange={(e) => setSelectedPlatformRole(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 bg-white mb-2"
            aria-label="Platform role">
            <option value="">No platform role</option>
            <option value="platform_admin">Platform admin</option>
            <option value="support">Support</option>
          </select>
          <p className="text-xs text-ds-text-muted mb-3">
            Platform roles grant access to the admin subdomain and all enterprise tenants.
          </p>
          <button onClick={handleSavePlatformRole} disabled={setPlatformRole.isPending}
            className="w-full py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            {setPlatformRole.isPending ? "Saving..." : "Save role"}
          </button>
        </SectionCard>
      </div>

      {/* Enterprise memberships + role editor — always shown */}
      <div className="grid grid-cols-2 gap-5">

        {/* Memberships card */}
        <SectionCard
          title="Enterprise memberships"
          action={
            availableToAdd.length > 0 && (
              <button onClick={() => { setShowAddForm((v) => !v); setAddEnterpriseId(""); setAddRoleIds([]); }}
                className="flex items-center gap-1 text-xs text-indigo-600 font-medium hover:text-indigo-700">
                {showAddForm ? <><X className="w-3 h-3" /> Cancel</> : <><Plus className="w-3 h-3" /> Add to enterprise</>}
              </button>
            )
          }
        >
          {/* Current memberships */}
          {user.enterprises.length === 0 && !showAddForm && (
            <div className="py-4 text-center">
              <p className="text-sm text-ds-text-muted">Not a member of any enterprise.</p>
              {availableToAdd.length > 0 && (
                <button onClick={() => setShowAddForm(true)}
                  className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">
                  <Plus className="w-4 h-4" /> Add to enterprise
                </button>
              )}
            </div>
          )}

          {user.enterprises.length > 0 && (
            <div className="divide-y divide-ds-border mb-4">
              {user.enterprises.map((e) => {
                const isDefault = user.defaultEnterpriseId === e.enterpriseId;
                return (
                <div key={e.enterpriseId} className="flex items-center justify-between py-3 gap-3">
                  {/* Default enterprise radio */}
                  <button
                    onClick={() => {
                      if (!isDefault) {
                        void setDefaultEnterprise.mutateAsync({ userId: user.id, enterpriseId: e.enterpriseId })
                          .then(() => toast({ title: `Default enterprise set to ${e.enterpriseName}`, variant: "success" }))
                          .catch(() => toast({ title: "Failed to update default enterprise", variant: "error" }));
                      }
                    }}
                    disabled={setDefaultEnterprise.isPending}
                    title={isDefault ? "Default login enterprise" : "Set as default login enterprise"}
                    className="flex-shrink-0 mt-0.5"
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                      isDefault ? "border-indigo-600 bg-indigo-600" : "border-ds-border-strong hover:border-indigo-400"
                    }`}>
                      {isDefault && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                  </button>

                  <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                      {e.enterpriseName.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-ds-text truncate">{e.enterpriseName}</p>
                        {isDefault && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-ds-text-muted">{e.enterpriseSlug}.tsnet.dev · <span className="capitalize">{e.status}</span></p>
                    </div>
                  </div>

                  <button onClick={() => handleRemoveMembership(e.enterpriseId, e.enterpriseName)}
                    disabled={removeMembership.isPending}
                    className="text-xs text-red-600 hover:text-red-700 font-medium disabled:opacity-50 flex-shrink-0">
                    Remove
                  </button>
                </div>
                );
              })}
            </div>
          )}

          {/* Add to enterprise form */}
          {showAddForm && (
            <div className="border border-indigo-200 rounded-xl bg-indigo-50/50 p-4 space-y-3">
              <p className="text-xs font-semibold text-ds-text-secondary">Add to enterprise</p>

              <div>
                <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Enterprise</label>
                <select value={addEnterpriseId} onChange={(e) => setAddEnterpriseId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 bg-white"
                  aria-label="Select enterprise">
                  <option value="">Select enterprise…</option>
                  {availableToAdd.map((e) => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">
                  Roles <span className="text-ds-text-muted font-normal">(select at least one)</span>
                </label>
                <div className="bg-white border border-ds-border rounded-lg overflow-hidden">
                  {allRoles.map((role) => {
                    const checked = addRoleIds.includes(role.id);
                    return (
                      <button key={role.id}
                        onClick={() => setAddRoleIds((prev) => prev.includes(role.id) ? prev.filter((r) => r !== role.id) : [...prev, role.id])}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-ds-bg border-b border-ds-border last:border-0 transition-colors">
                        <div className={cn(
                          "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors",
                          checked ? "bg-indigo-600 border-indigo-600" : "border-ds-border-strong"
                        )}>
                          {checked && <Check className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <div>
                          <p className="text-sm text-ds-text">{role.name}</p>
                          {role.moduleKey && <p className="text-xs text-ds-text-muted capitalize">{role.moduleKey} module</p>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <button onClick={handleAddToEnterprise}
                disabled={addToEnterprise.isPending || !addEnterpriseId || addRoleIds.length === 0}
                className="w-full py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                {addToEnterprise.isPending ? "Adding..." : "Add to enterprise"}
              </button>
            </div>
          )}
        </SectionCard>

        {/* Role editor for existing memberships */}
        {user.enterprises.length > 0 ? (
          <SectionCard
            title={`Roles in ${activeEntName}`}
            action={
              user.enterprises.length > 1 ? (
                <select value={activeEnterpriseId ?? ""}
                  onChange={(e) => changeActiveEnterprise(e.target.value)}
                  className="text-xs text-indigo-600 border-none bg-transparent outline-none cursor-pointer font-medium"
                  aria-label="Switch enterprise">
                  {user.enterprises.map((e) => (
                    <option key={e.enterpriseId} value={e.enterpriseId}>{e.enterpriseName}</option>
                  ))}
                </select>
              ) : undefined
            }
          >
            <div className="mb-4">
              <RoleCheckList
                roles={allRoles}
                selectedIds={selectedRoleIds}
                onToggle={(id) => setSelectedRoleIds((prev) =>
                  prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
                )}
              />
            </div>
            <button onClick={handleSaveRoles}
              disabled={assignRoles.isPending || !activeEnterpriseId}
              className="w-full py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
              {assignRoles.isPending ? "Saving..." : "Save roles"}
            </button>
          </SectionCard>
        ) : (
          /* No enterprise memberships — roles assignable here are
             platform-level (enterpriseId null), scoped to whichever
             roles an admin has marked individuallyAssignable via Role
             builder (backend-enforced in PATCH .../platform-roles, not
             just this filter). ConfigIQ User is seeded with this flag
             set, so individual ConfigIQ access keeps working exactly as
             before — this is no longer ConfigIQ-specific machinery. */
          <SectionCard title="Roles">
            <p className="text-xs text-ds-text-muted mb-3">
              This user has no enterprise. Roles assigned here apply when they work individually on app.tsnet.dev.
              Only roles marked &quot;assignable without an enterprise&quot; appear here — set that in{" "}
              <a href="/platform/roles" className="text-indigo-600 hover:underline">Role builder</a>.
            </p>
            <div className="mb-4">
              <RoleCheckList
                roles={individuallyAssignableRoles}
                selectedIds={platformRoleIds}
                onToggle={(id) => setPlatformRoleIds((prev) =>
                  prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
                )}
              />
            </div>
            <button onClick={handleSavePlatformRoles}
              disabled={assignPlatformRoles.isPending}
              className="w-full py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
              {assignPlatformRoles.isPending ? "Saving..." : "Save roles"}
            </button>
          </SectionCard>
        )}
      </div>

      {/* Storage limit — platform admin only, and never applied to other
          platform admins (they have no limit, so setting one is a no-op
          in the service layer, but we still show the UI for clarity). */}
      {user && user.platformRole !== "platform_admin" && (
        <div className="bg-white border border-ds-border rounded-card shadow-card p-5 space-y-3">
          <h2 className="text-sm font-semibold text-ds-text">Storage limit</h2>
          <p className="text-xs text-ds-text-muted">
            Overrides the enterprise and platform defaults. Leave blank to use the enterprise default
            {enterpriseStorage?.perUserLimitBytes
              ? ` (${Math.round(enterpriseStorage.perUserLimitBytes / (1024 * 1024))} MB)`
              : " (platform default)"}
            .
          </p>
          {(() => {
            if (user && !storageLimitSynced) {
              const limitMb = (user as unknown as { storageLimitBytes?: number | null }).storageLimitBytes;
              setStorageLimitMb(limitMb ? String(Math.round(limitMb / (1024 * 1024))) : "");
              setStorageLimitSynced(true);
            }
            return null;
          })()}
          <div className="flex items-center gap-3">
            <input
              type="number" min={1} value={storageLimitMb}
              onChange={(e) => setStorageLimitMb(e.target.value)}
              placeholder="MB (blank = use enterprise/platform default)"
              className="flex-1 h-9 rounded-lg border border-ds-border px-3 text-sm"
            />
            <button
              onClick={async () => {
                try {
                  await updateStorageLimit.mutateAsync({
                    userId: user.id,
                    storageLimitBytes: storageLimitMb ? Number(storageLimitMb) * 1024 * 1024 : null,
                  });
                  toast({ title: "Storage limit updated", variant: "success" });
                } catch {
                  toast({ title: "Failed to update storage limit", variant: "error" });
                }
              }}
              disabled={updateStorageLimit.isPending}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              Save
            </button>
            {storageLimitMb && (
              <button
                onClick={async () => {
                  await updateStorageLimit.mutateAsync({ userId: user.id, storageLimitBytes: null });
                  setStorageLimitMb("");
                  toast({ title: "Storage limit cleared — using default", variant: "success" });
                }}
                className="text-xs text-ds-text-muted hover:text-red-600"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Danger zone */}
      <div className="bg-white border border-red-200 rounded-card shadow-card p-5">
        <h2 className="text-xs font-semibold text-red-600 uppercase tracking-wide flex items-center gap-1.5 mb-4">
          <AlertTriangle className="w-3.5 h-3.5" /> Danger zone
        </h2>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-ds-text">Delete user</p>
            <p className="text-xs text-ds-text-muted mt-0.5">
              Permanently removes this user, all enterprise memberships, and all sessions. Cannot be undone.
            </p>
          </div>
          <button onClick={() => setShowDeleteConfirm(true)}
            className="flex-shrink-0 px-3.5 py-2 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 text-sm font-medium">
            Delete user
          </button>
        </div>
        {showDeleteConfirm && (
          <div className="mt-4 p-4 rounded-lg border border-red-200 bg-red-50 space-y-3">
            <p className="text-sm text-red-700">Type <strong>DELETE</strong> to confirm. This cannot be undone.</p>
            <input value={deleteText} onChange={(e) => setDeleteText(e.target.value)}
              placeholder="Type DELETE"
              className="w-full px-3 py-2 text-sm border border-red-300 rounded-lg focus:outline-none focus:border-red-500 bg-white" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setShowDeleteConfirm(false); setDeleteText(""); }}
                className="px-3 py-1.5 rounded-lg bg-white border border-ds-border text-sm text-ds-text-secondary hover:bg-ds-bg">
                Cancel
              </button>
              <button onClick={handleDeleteUser}
                disabled={deleteText !== "DELETE" || deleteUser.isPending}
                className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-700">
                {deleteUser.isPending ? "Deleting..." : "Confirm delete"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
